import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConfirmDialog from '../UI/ConfirmDialog';
import {
  collection, addDoc, updateDoc, doc, deleteDoc, getDocs, query, where,
  getDoc, setDoc, onSnapshot, orderBy,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { logAudit } from '../../utils/auditLog';

// ── small reusable bits ──────────────────────────────────────────────────────

const Label = ({ children }) => (
  <span className="block text-xs font-semibold text-slate-600 mb-1">{children}</span>
);

const Input = (props) => (
  <input
    {...props}
    className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
  />
);

const Btn = ({ children, onClick, variant = 'primary', size = 'sm', disabled }) => {
  const base = 'rounded-lg font-medium transition-colors disabled:opacity-50';
  const sz = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  const col =
    variant === 'primary' ? 'bg-blue-600 text-white hover:bg-blue-700' :
    variant === 'danger'  ? 'bg-red-50 text-red-600 hover:bg-red-100' :
    'border border-gray-300 text-gray-700 hover:bg-gray-50';
  return (
    <button className={`${base} ${sz} ${col}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

const Badge = ({ active }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
    active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
  }`}>
    {active ? 'Active' : 'Disabled'}
  </span>
);

// ── Accounts tab ─────────────────────────────────────────────────────────────

const AccountsTab = ({ orgId, accounts }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    await addDoc(collection(db, 'orgs', orgId, 'accounts'), {
      name: n, active: true, order: accounts.length,
    });
    await logAudit(orgId, 'Account Added', { name: n });
    setName('');
    setSaving(false);
  };

  const toggle = async (acct) => {
    await updateDoc(doc(db, 'orgs', orgId, 'accounts', acct.id), { active: !acct.active });
  };

  const rename = async (acct, newName) => {
    const n = newName.trim();
    if (!n || n === acct.name) return;
    await updateDoc(doc(db, 'orgs', orgId, 'accounts', acct.id), { name: n });
  };

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Bank accounts used for transactions and reconciliation.
      </p>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="bg-slate-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Name</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Status</th>
            <th className="px-4 py-2 no-print"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {accounts.map(a => (
            <tr key={a.id} className="hover:bg-slate-50">
              <td className="px-4 py-2">
                <InlineEdit value={a.name} onSave={(v) => rename(a, v)} />
              </td>
              <td className="px-4 py-2"><Badge active={a.active} /></td>
              <td className="px-4 py-2 text-right">
                <Btn variant="ghost" onClick={() => toggle(a)}>
                  {a.active ? 'Disable' : 'Enable'}
                </Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Label>New Account Name</Label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && add()}
            placeholder="e.g. Money Market"
          />
        </div>
        <Btn size="md" onClick={add} disabled={saving || !name.trim()}>Add Account</Btn>
      </div>
    </div>
  );
};

// ── Funds tab ─────────────────────────────────────────────────────────────────

const FundsTab = ({ orgId, funds, accounts }) => {
  const [name, setName] = useState('');
  const [newAccount, setNewAccount] = useState('');
  const [saving, setSaving] = useState(false);
  const activeAccounts = accounts.filter(a => a.active !== false);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    await addDoc(collection(db, 'orgs', orgId, 'funds'), {
      name: n, active: true, order: funds.length, account: newAccount,
    });
    await logAudit(orgId, 'Fund Added', { name: n, account: newAccount });
    setName('');
    setSaving(false);
  };

  const toggle = (fund) =>
    updateDoc(doc(db, 'orgs', orgId, 'funds', fund.id), { active: !fund.active });

  const rename = (fund, newName) => {
    const n = newName.trim();
    if (!n || n === fund.name) return;
    updateDoc(doc(db, 'orgs', orgId, 'funds', fund.id), { name: n });
  };

  const linkAccount = (fund, acct) =>
    updateDoc(doc(db, 'orgs', orgId, 'funds', fund.id), { account: acct });

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Funds group transactions for reporting. Link each fund to the financial account it belongs to.
      </p>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="bg-slate-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Fund Name</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Linked Account</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Status</th>
            <th className="px-4 py-2 no-print"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {funds.map(f => (
            <tr key={f.id} className="hover:bg-slate-50">
              <td className="px-4 py-2">
                <InlineEdit value={f.name} onSave={(v) => rename(f, v)} />
              </td>
              <td className="px-4 py-2">
                <select
                  value={f.account || ''}
                  onChange={e => linkAccount(f, e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <option value="">— unlinked —</option>
                  {activeAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </td>
              <td className="px-4 py-2"><Badge active={f.active} /></td>
              <td className="px-4 py-2 text-right">
                <Btn variant="ghost" onClick={() => toggle(f)}>
                  {f.active ? 'Disable' : 'Enable'}
                </Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Label>New Fund Name</Label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && add()}
            placeholder="e.g. Youth Group"
          />
        </div>
        <div>
          <Label>Linked Account</Label>
          <select
            value={newAccount}
            onChange={e => setNewAccount(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option value="">— unlinked —</option>
            {activeAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
          </select>
        </div>
      </div>
      <Btn size="md" onClick={add} disabled={saving || !name.trim()}>Add Fund</Btn>
    </div>
  );
};

// ── Transaction Types tab ────────────────────────────────────────────────────

const TypesTab = ({ orgId, transactionTypes }) => {
  const [form, setForm] = useState({ name: '', kind: 'expense' });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type, usageCount }

  const add = async () => {
    const n = form.name.trim();
    if (!n) return;
    setSaving(true);
    await addDoc(collection(db, 'orgs', orgId, 'transactionTypes'), {
      name: n,
      kind: form.kind,
      active: true,
      order: transactionTypes.length,
    });
    setForm({ name: '', kind: 'expense' });
    setSaving(false);
  };

  const toggle = (t) =>
    updateDoc(doc(db, 'orgs', orgId, 'transactionTypes', t.id), { active: !t.active });

  const deletetype = async (t) => {
    const snap = await getDocs(
      query(collection(db, 'orgs', orgId, 'transactions'), where('type', '==', t.name))
    );
    setConfirm({ type: t, usageCount: snap.size });
  };

  const confirmDelete = async () => {
    await deleteDoc(doc(db, 'orgs', orgId, 'transactionTypes', confirm.type.id));
    setConfirm(null);
  };

  const renametype = (t, newName) => {
    const n = newName.trim();
    if (!n || n === t.name) return;
    updateDoc(doc(db, 'orgs', orgId, 'transactionTypes', t.id), { name: n });
  };


  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Categories for transactions. Hover a name to edit it.
      </p>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="bg-slate-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Name</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Type</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Status</th>
            <th className="px-4 py-2 no-print"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {transactionTypes.map(t => (
            <tr key={t.id} className={`hover:bg-slate-50 ${!t.active ? 'opacity-50' : ''}`}>
              <td className="px-4 py-2">
                <InlineEdit value={t.name} onSave={(v) => renametype(t, v)} />
              </td>
              <td className="px-4 py-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  t.kind === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                }`}>
                  {t.kind === 'income' ? 'Money In' : 'Money Out'}
                </span>
              </td>
              <td className="px-4 py-2"><Badge active={t.active} /></td>
              <td className="px-4 py-2 text-right flex gap-2 justify-end">
                <Btn variant="ghost" onClick={() => toggle(t)}>
                  {t.active ? 'Disable' : 'Enable'}
                </Btn>
                <Btn variant="danger" onClick={() => deletetype(t)}>Delete</Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-gray-200 pt-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Add Category</h4>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder="e.g. CMP"
            />
          </div>
          <div>
            <Label>Type</Label>
            <select
              value={form.kind}
              onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="expense">Money Out</option>
              <option value="income">Money In</option>
            </select>
          </div>
        </div>
        <Btn size="md" onClick={add} disabled={saving || !form.name.trim()}>Add Category</Btn>
      </div>

      {confirm && (
        confirm.usageCount > 0 ? (
          <ConfirmDialog
            title={`Cannot delete "${confirm.type.name}"`}
            message={`This category is used in ${confirm.usageCount} transaction${confirm.usageCount !== 1 ? 's' : ''} and cannot be deleted. Disable it instead to hide it from new transactions.`}
            onConfirm={() => setConfirm(null)}
            onCancel={() => setConfirm(null)}
            confirmLabel="OK"
            danger={false}
          />
        ) : (
          <ConfirmDialog
            title={`Delete "${confirm.type.name}"?`}
            message="This category has no transactions and will be permanently removed."
            onConfirm={confirmDelete}
            onCancel={() => setConfirm(null)}
            confirmLabel="Delete"
            danger
          />
        )
      )}
    </div>
  );
};

// ── Inline edit helpers ───────────────────────────────────────────────────────

const InlineEdit = ({ value, onSave, inputProps = {} }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  const commit = () => {
    if (val !== value) onSave(val);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="border border-blue-400 rounded-lg px-2 py-1 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
          {...inputProps}
        />
        <button
          onClick={commit}
          className="text-xs bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700"
        >
          Save
        </button>
        <button
          onClick={() => setEditing(false)}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 group">
      <span className="text-slate-800">{value}</span>
      <button
        onClick={() => { setVal(value); setEditing(true); }}
        className="text-xs text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        Edit
      </button>
    </div>
  );
};

// ── Budget tab ────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [2023, 2024, 2025, 2026, 2027, 2028];

const fmt = (n) => n > 0
  ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—';

// A budget line: { id (local), fund, type, monthly }
const emptyLine = () => ({ id: Date.now() + Math.random(), fund: '', type: '', monthly: '' });

const BudgetTab = ({ orgId, funds, transactionTypes }) => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [lines, setLines] = useState([]);   // working lines
  const [saving, setSaving] = useState(false);

  const activeFunds = funds.filter(f => f.active !== false);
  const activeTypes = transactionTypes.filter(t => t.active !== false);

  // Load saved budget for this year
  useEffect(() => {
    getDoc(doc(db, 'orgs', orgId, 'budgets', String(year))).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setLines((data.lines || []).map(l => ({ ...l, id: Date.now() + Math.random() })));
      } else {
        setLines([]);
      }
    });
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const addLine = () => setLines(ls => [...ls, emptyLine()]);

  const removeLine = (id) => setLines(ls => ls.filter(l => l.id !== id));

  const updateLine = (id, field, value) =>
    setLines(ls => ls.map(l => l.id === id ? { ...l, [field]: value } : l));

  const save = async () => {
    setSaving(true);
    const toSave = lines
      .filter(l => l.fund && l.type)
      .map(({ fund, type, monthly }) => ({ fund, type, monthly: Number(monthly) || 0 }));
    await setDoc(doc(db, 'orgs', orgId, 'budgets', String(year)), { lines: toSave });
    await logAudit(orgId, 'Budget Saved', { year, lineCount: toSave.length });
    setSaving(false);
  };

  // Totals
  const totalMonthly = lines.reduce((s, l) => s + (Number(l.monthly) || 0), 0);
  const totalAnnual  = totalMonthly * 12;

  // Types available for a given fund (no fund filter since types are now flat)
  const typesForKind = (kind) => activeTypes.filter(t => t.kind === kind);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">
          Budget lines by fund and category. Monthly × 12 = annual.
        </p>
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <Btn size="md" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Budget'}
          </Btn>
        </div>
      </div>

      <table className="w-full text-sm mb-3">
        <thead>
          <tr className="bg-slate-50 border-b border-gray-200 text-slate-600">
            <th className="px-3 py-2 text-left font-semibold">Fund</th>
            <th className="px-3 py-2 text-left font-semibold">Category</th>
            <th className="px-3 py-2 text-left font-semibold">Type</th>
            <th className="px-3 py-2 text-right font-semibold w-36">Monthly</th>
            <th className="px-3 py-2 text-right font-semibold w-36">Annual</th>
            <th className="px-3 py-2 w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {lines.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-10 text-center text-slate-500 text-sm">
                No budget lines yet. Press <strong>+</strong> to add one.
              </td>
            </tr>
          )}
          {lines.map(line => {
            const kindLabel = line.type
              ? (activeTypes.find(t => t.name === line.type)?.kind === 'income' ? 'Money In' : 'Money Out')
              : '';
            return (
              <tr key={line.id} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <select
                    value={line.fund}
                    onChange={e => updateLine(line.id, 'fund', e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select fund…</option>
                    {activeFunds.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={line.type}
                    onChange={e => updateLine(line.id, 'type', e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select category…</option>
                    <optgroup label="Money Out">
                      {typesForKind('expense').map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                    </optgroup>
                    <optgroup label="Money In">
                      {typesForKind('income').map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                    </optgroup>
                  </select>
                </td>
                <td className="px-3 py-2">
                  {kindLabel && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      kindLabel === 'Money In' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>
                      {kindLabel}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={line.monthly}
                      onChange={e => updateLine(line.id, 'monthly', e.target.value)}
                      placeholder="0.00"
                      className="border border-gray-300 rounded-lg pl-6 pr-2 py-1.5 text-sm text-right w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-sm text-slate-600">
                  {fmt((Number(line.monthly) || 0) * 12)}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => removeLine(line.id)}
                    className="w-6 h-6 rounded-full bg-red-100 text-red-500 hover:bg-red-200 font-bold text-sm leading-none flex items-center justify-center"
                  >
                    −
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        {lines.length > 0 && (
          <tfoot>
            <tr className="bg-slate-100 border-t-2 border-slate-200 font-semibold text-sm">
              <td colSpan={3} className="px-3 py-2.5 text-slate-500">Total</td>
              <td className="px-3 py-2.5 text-right font-mono text-slate-700">{fmt(totalMonthly)}</td>
              <td className="px-3 py-2.5 text-right font-mono text-slate-700">{fmt(totalAnnual)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>

      <button
        onClick={addLine}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium px-1"
      >
        <span className="w-6 h-6 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center font-bold text-base leading-none">+</span>
        Add budget line
      </button>
    </div>
  );
};

// ── Users tab ─────────────────────────────────────────────────────────────────

const UsersTab = ({ orgId }) => {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null); // user doc to remove
  const me = auth.currentUser?.email;

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'users'), (snap) => {
      setUsers(snap.docs.map(d => ({ email: d.id, ...d.data() }))
        .sort((a, b) => a.email.localeCompare(b.email)));
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addUser = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    if (users.find(u => u.email === e)) { setError('That email is already added.'); return; }
    setSaving(true);
    setError('');
    await setDoc(doc(db, 'orgs', orgId, 'users', e), { role });
    await logAudit(orgId, 'User Added', { email: e, role });
    setEmail('');
    setRole('user');
    setSaving(false);
  };

  const changeRole = (u, newRole) =>
    updateDoc(doc(db, 'orgs', orgId, 'users', u.email), { role: newRole });

  const removeUser = async () => {
    await logAudit(orgId, 'User Removed', { email: confirm.email });
    await deleteDoc(doc(db, 'orgs', orgId, 'users', confirm.email));
    setConfirm(null);
  };

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Manage who can access this organization. Users sign in with Google using the email listed here.
      </p>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="bg-slate-50 border-b border-gray-200">
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Email</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-600">Role</th>
            <th className="px-4 py-2 no-print"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {users.map(u => (
            <tr key={u.email} className="hover:bg-slate-50">
              <td className="px-4 py-2 text-slate-700">
                {u.email}
                {u.email === me && (
                  <span className="ml-2 text-xs text-blue-500 font-medium">(you)</span>
                )}
              </td>
              <td className="px-4 py-2">
                <select
                  value={u.role || 'user'}
                  onChange={e => changeRole(u, e.target.value)}
                  disabled={u.email === me}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td className="px-4 py-2 text-right">
                {u.email !== me && (
                  <Btn variant="danger" onClick={() => setConfirm(u)}>Remove</Btn>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      <div className="border-t border-gray-200 pt-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Add User</h4>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Email Address</Label>
            <Input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && addUser()}
              placeholder="user@example.com"
            />
          </div>
          <div>
            <Label>Role</Label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <Btn size="md" onClick={addUser} disabled={saving || !email.trim()}>Add User</Btn>
        </div>
      </div>

      {confirm && (
        <ConfirmDialog
          title={`Remove ${confirm.email}?`}
          message="This user will immediately lose access to the organization."
          onConfirm={removeUser}
          onCancel={() => setConfirm(null)}
          confirmLabel="Remove"
          danger
        />
      )}
    </div>
  );
};

// ── Donors tab ───────────────────────────────────────────────────────────────

const emptyDonor = () => ({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });

const DonorsTab = ({ orgId }) => {
  const [donors, setDonors]   = useState([]);
  const [form, setForm]       = useState(emptyDonor());
  const [editing, setEditing] = useState(null); // donor id being edited
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'donors'), snap => {
      setDonors(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)));
    });
    return unsub;
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (editing) {
      await updateDoc(doc(db, 'orgs', orgId, 'donors', editing), { ...form, name: form.name.trim() });
      await logAudit(orgId, 'Donor Edited', { name: form.name.trim() });
    } else {
      await addDoc(collection(db, 'orgs', orgId, 'donors'), { ...form, name: form.name.trim() });
      await logAudit(orgId, 'Donor Added', { name: form.name.trim() });
    }
    setForm(emptyDonor());
    setEditing(null);
    setSaving(false);
  };

  const startEdit = (donor) => {
    setEditing(donor.id);
    setForm({ name: donor.name || '', email: donor.email || '', phone: donor.phone || '', address: donor.address || '', city: donor.city || '', state: donor.state || '', zip: donor.zip || '' });
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyDonor()); };

  const remove = async (donor) => {
    await logAudit(orgId, 'Donor Deleted', { name: donor.name });
    deleteDoc(doc(db, 'orgs', orgId, 'donors', donor.id));
  };

  const field = (label, key, placeholder, half = false) => (
    <div className={half ? '' : 'col-span-2'}>
      <Label>{label}</Label>
      <Input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} />
    </div>
  );

  const visible = donors.filter(d => !search || d.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700">{donors.length} Donor{donors.length !== 1 ? 's' : ''}</h3>
        <input
          type="text"
          placeholder="Search donors…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="bg-slate-50 text-slate-600 text-left">
            <th className="px-4 py-2 font-semibold">Name</th>
            <th className="px-4 py-2 font-semibold">Email</th>
            <th className="px-4 py-2 font-semibold">Phone</th>
            <th className="px-4 py-2 font-semibold">City</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {visible.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-sm">No donors yet.</td></tr>
          )}
          {visible.map(d => (
            <tr key={d.id} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-medium text-slate-700">{d.name}</td>
              <td className="px-4 py-2 text-slate-500">{d.email || '—'}</td>
              <td className="px-4 py-2 text-slate-500">{d.phone || '—'}</td>
              <td className="px-4 py-2 text-slate-500">{d.city || '—'}</td>
              <td className="px-4 py-2 text-right flex gap-2 justify-end">
                <Btn variant="ghost" onClick={() => startEdit(d)}>Edit</Btn>
                <Btn variant="danger" onClick={() => remove(d)}>Delete</Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-gray-200 pt-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">{editing ? 'Edit Donor' : 'Add Donor'}</h4>
        <div className="grid grid-cols-2 gap-3 mb-3">
          {field('Name *', 'name', 'Full name or organization')}
          {field('Email', 'email', 'email@example.com', true)}
          {field('Phone', 'phone', '(555) 000-0000', true)}
          {field('Address', 'address', '123 Main St')}
          {field('City', 'city', 'City', true)}
          {field('State', 'state', 'ME', true)}
          {field('ZIP', 'zip', '04001', true)}
        </div>
        <div className="flex gap-2">
          <Btn size="md" onClick={save} disabled={saving || !form.name.trim()}>
            {editing ? 'Save Changes' : 'Add Donor'}
          </Btn>
          {editing && <Btn size="md" variant="ghost" onClick={cancelEdit}>Cancel</Btn>}
        </div>
      </div>
    </div>
  );
};

// ── Payees tab ────────────────────────────────────────────────────────────────
// Payee is still free text on transactions (payableTo) — this collection is an
// enrichment layer keyed by exact name match, used for 1099 prep (contact info,
// tax ID). New payees are auto-created (name only) the first time someone types
// a new name into a transaction; this tab is where you fill in the rest.

const emptyPayee = () => ({
  name: '', businessName: '', address: '', city: '', state: '', zip: '',
  phone: '', email: '', tin: '', tinType: 'EIN', exempt1099: true, notes: '',
});

const maskTin = (tin) => {
  if (!tin) return '—';
  const digits = tin.replace(/\D/g, '');
  return digits.length >= 4 ? `•••••${digits.slice(-4)}` : '•••';
};

const PayeesTab = ({ orgId, initialSearch = '' }) => {
  const [payees, setPayees]   = useState([]);
  const [form, setForm]       = useState(emptyPayee());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState(initialSearch);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'payees'), snap => {
      setPayees(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)));
    });
    return unsub;
  }, [orgId]);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (editing) {
      await updateDoc(doc(db, 'orgs', orgId, 'payees', editing), { ...form, name: form.name.trim() });
      await logAudit(orgId, 'Payee Edited', { name: form.name.trim() });
    } else {
      await addDoc(collection(db, 'orgs', orgId, 'payees'), { ...form, name: form.name.trim() });
      await logAudit(orgId, 'Payee Added', { name: form.name.trim() });
    }
    setForm(emptyPayee());
    setEditing(null);
    setSaving(false);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...emptyPayee(), ...p });
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyPayee()); };

  const remove = async (p) => {
    await logAudit(orgId, 'Payee Deleted', { name: p.name });
    deleteDoc(doc(db, 'orgs', orgId, 'payees', p.id));
  };

  const toggleExempt = (p) =>
    updateDoc(doc(db, 'orgs', orgId, 'payees', p.id), { exempt1099: !p.exempt1099 });

  const field = (label, key, placeholder, half = false) => (
    <div className={half ? '' : 'col-span-2'}>
      <Label>{label}</Label>
      <Input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} />
    </div>
  );

  const visible = payees.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700">{payees.length} Payee{payees.length !== 1 ? 's' : ''}</h3>
        <input
          type="text"
          placeholder="Search payees…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="bg-slate-50 text-slate-600 text-left">
            <th className="px-4 py-2 font-semibold">Name</th>
            <th className="px-4 py-2 font-semibold">Phone</th>
            <th className="px-4 py-2 font-semibold">TIN</th>
            <th className="px-4 py-2 font-semibold">1099 Exempt</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {visible.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-sm">
              {payees.length === 0 ? 'No payees yet — they\'re added automatically the first time you use a name on a Money Out transaction.' : 'No payees match your search.'}
            </td></tr>
          )}
          {visible.map(p => (
            <tr key={p.id} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-medium text-slate-700">
                {p.name}
                {p.businessName && <span className="text-slate-400 font-normal"> · {p.businessName}</span>}
              </td>
              <td className="px-4 py-2 text-slate-500">{p.phone || '—'}</td>
              <td className="px-4 py-2 text-slate-500 font-mono text-xs">{maskTin(p.tin)}</td>
              <td className="px-4 py-2">
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                  <input type="checkbox" checked={!!p.exempt1099} onChange={() => toggleExempt(p)} className="rounded" />
                  <span className="text-xs text-slate-500">{p.exempt1099 ? 'Exempt' : 'Not exempt'}</span>
                </label>
              </td>
              <td className="px-4 py-2 text-right flex gap-2 justify-end">
                <Btn variant="ghost" onClick={() => startEdit(p)}>Edit</Btn>
                <Btn variant="danger" onClick={() => remove(p)}>Delete</Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-gray-200 pt-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">{editing ? 'Edit Payee' : 'Add Payee'}</h4>
        <p className="text-xs text-slate-500 mb-3">
          Used for 1099 prep. Tax ID is sensitive — only admins can see this tab.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          {field('Legal Name *', 'name', 'Individual or business name')}
          {field('Business / DBA Name', 'businessName', '(if different)')}
          {field('Phone', 'phone', '(555) 000-0000', true)}
          {field('Email', 'email', 'email@example.com', true)}
          {field('Address', 'address', '123 Main St')}
          {field('City', 'city', 'City', true)}
          {field('State', 'state', 'ME', true)}
          {field('ZIP', 'zip', '04001', true)}
          <div>
            <Label>TIN Type</Label>
            <select
              value={form.tinType}
              onChange={e => setForm(f => ({ ...f, tinType: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="EIN">EIN</option>
              <option value="SSN">SSN</option>
            </select>
          </div>
          {field('Tax ID (TIN)', 'tin', 'XX-XXXXXXX or XXX-XX-XXXX', true)}
          {field('Notes', 'notes', 'Optional')}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none mb-4">
          <input
            type="checkbox" checked={!!form.exempt1099}
            onChange={e => setForm(f => ({ ...f, exempt1099: e.target.checked }))}
            className="rounded"
          />
          <span>Exempt from 1099 reporting</span>
        </label>
        <div className="flex gap-2">
          <Btn size="md" onClick={save} disabled={saving || !form.name.trim()}>
            {editing ? 'Save Changes' : 'Add Payee'}
          </Btn>
          {editing && <Btn size="md" variant="ghost" onClick={cancelEdit}>Cancel</Btn>}
        </div>
      </div>
    </div>
  );
};

// ── Organization tab ─────────────────────────────────────────────────────────

const OrgTab = ({ orgId }) => {
  const settings = useOrgSettings(orgId);
  const [orgName, setOrgName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync local state when settings load
  useEffect(() => {
    setOrgName(settings.orgName || '');
  }, [settings.orgName]);

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    const storageRef = ref(storage, `orgs/${orgId}/logo`);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      (snap) => setProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      (err) => { console.error('Upload error:', err); setUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        await setDoc(doc(db, 'orgs', orgId, '_config', 'settings'), { logoUrl: url }, { merge: true });
        setUploading(false);
      }
    );
  };

  const saveOrgName = async () => {
    setSaving(true);
    const trimmed = orgName.trim();
    await Promise.all([
      setDoc(doc(db, 'orgs', orgId, '_config', 'settings'), { orgName: trimmed }, { merge: true }),
      setDoc(doc(db, 'orgs', orgId), { name: trimmed }, { merge: true }),
    ]);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-8 max-w-lg">
      {/* Org Name */}
      <div>
        <Label>Organization Name</Label>
        <p className="text-xs text-slate-500 mb-2">Displayed in the sidebar header.</p>
        <div className="flex gap-2">
          <Input
            value={orgName}
            onChange={e => setOrgName(e.target.value)}
            placeholder="e.g. Sidney Baptist Church"
            onKeyDown={e => e.key === 'Enter' && saveOrgName()}
          />
          <Btn size="md" onClick={saveOrgName} disabled={saving || !orgName.trim()}>
            {saved ? 'Saved!' : saving ? 'Saving…' : 'Save'}
          </Btn>
        </div>
      </div>

      {/* Logo */}
      <div>
        <Label>Organization Logo</Label>
        <p className="text-xs text-slate-500 mb-3">Shown in the sidebar. Recommended: square PNG or SVG, at least 64×64px.</p>
        {settings.logoUrl && (
          <img
            src={settings.logoUrl}
            alt="Org logo"
            className="w-16 h-16 rounded-xl object-contain border border-gray-200 mb-3 bg-white"
          />
        )}
        <label className="cursor-pointer inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm text-slate-700 hover:bg-gray-50 transition-colors">
          <span>{uploading ? `Uploading… ${progress}%` : settings.logoUrl ? 'Replace Logo' : 'Upload Logo'}</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoChange}
            disabled={uploading}
          />
        </label>
        {uploading && (
          <div className="mt-2 w-48 bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Audit Log tab ─────────────────────────────────────────────────────────────

const AuditLogTab = ({ orgId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'orgs', orgId, 'auditLog'),
      orderBy('timestamp', 'desc'),
    );
    const unsub = onSnapshot(q, snap => {
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-slate-500 text-sm py-8 text-center">Loading…</div>;
  if (entries.length === 0) return <div className="text-slate-500 text-sm py-8 text-center">No audit log entries yet.</div>;

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">A record of all actions performed in this organization.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide border-b border-gray-200">
              <th className="px-4 py-2.5 text-left">Date & Time</th>
              <th className="px-4 py-2.5 text-left">Event</th>
              <th className="px-4 py-2.5 text-left">User</th>
              <th className="px-4 py-2.5 text-left">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.map(e => {
              const ts = e.timestamp?.toDate?.();
              const dateStr = ts
                ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—';
              const timeStr = ts
                ? ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                : '';
              const details = Object.entries(e.details || {})
                .map(([k, v]) => `${k}: ${v}`)
                .join(' · ');
              return (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 whitespace-nowrap text-slate-500 font-mono text-xs">
                    <div>{dateStr}</div>
                    <div className="text-slate-500">{timeStr}</div>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-700 whitespace-nowrap">{e.event}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{e.user?.email || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{details}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Main Admin page ───────────────────────────────────────────────────────────

const TABS = ['Accounts', 'Funds', 'Transaction Types', 'Budget', 'Payees', 'Donors', 'Users', 'Organization', 'Audit Log'];

const Admin = () => {
  const orgId = useOrgId();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'Accounts');
  const { accounts, funds, transactionTypes, loading } = useOrgData(orgId);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64 text-slate-500 text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Admin</h2>
        <p className="text-slate-500 text-sm mt-1">Manage accounts, funds, and transaction types for your organization.</p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        {tab === 'Accounts'          && <AccountsTab orgId={orgId} accounts={accounts} />}
        {tab === 'Funds'             && <FundsTab orgId={orgId} funds={funds} accounts={accounts} />}
        {tab === 'Transaction Types' && <TypesTab orgId={orgId} transactionTypes={transactionTypes} />}
        {tab === 'Budget'            && <BudgetTab orgId={orgId} funds={funds} transactionTypes={transactionTypes} />}
        {tab === 'Payees'            && <PayeesTab orgId={orgId} initialSearch={searchParams.get('search') || ''} />}
        {tab === 'Donors'            && <DonorsTab orgId={orgId} />}
        {tab === 'Users'             && <UsersTab orgId={orgId} />}
        {tab === 'Organization'      && <OrgTab orgId={orgId} />}
        {tab === 'Audit Log'         && <AuditLogTab orgId={orgId} />}
      </div>
    </div>
  );
};

export default Admin;
